/**
 * Phase 11 / Task 11.3 (DEC-124) — InReview flag acknowledgments.
 *
 * One row per acknowledged flag on a ticket. A truck pusher must explicitly
 * acknowledge every ack-required flag (currently just GPS_OUTSIDE_RANGE) before
 * the ticket can be approved. UNIQUE (job_id, flag_code) makes acknowledgment
 * idempotent — a re-ack is a no-op, never a duplicate row. RLS-scoped by tenant.
 *
 * The flag_code is stored as free text (matching the jobs.inreview_flags jsonb
 * codes); it is validated against INREVIEW_FLAG_CODES at the API boundary.
 */

import { pgTable, uuid, text, timestamp, unique, index, pgPolicy } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { jobs } from './jobs.js';

export const inreviewFlagAcknowledgments = pgTable(
  'inreview_flag_acknowledgments',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    jobId: uuid('job_id')
      .notNull()
      .references(() => jobs.id, { onDelete: 'cascade' }),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    flagCode: text('flag_code').notNull(),
    acknowledgedBy: uuid('acknowledged_by')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    acknowledgedAt: timestamp('acknowledged_at', { withTimezone: true }).notNull().defaultNow(),
    notes: text('notes'),
  },
  (t) => [
    unique('inreview_flag_ack_job_flag_unique').on(t.jobId, t.flagCode),
    index('inreview_flag_ack_tenant_job_idx').on(t.tenantId, t.jobId),
    pgPolicy('inreview_flag_ack_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type InreviewFlagAcknowledgment = typeof inreviewFlagAcknowledgments.$inferSelect;
export type NewInreviewFlagAcknowledgment = typeof inreviewFlagAcknowledgments.$inferInsert;
